import { Text } from '@blocksuite/affine/store';
import {
  createDatabaseBlockDataSource,
  isDatabaseStorageModelReady,
  observeDatabaseStorageModelReady,
} from '@blocksuite/affine-block-database';
import {
  DATABASE_ROW_SHARD_ORDER_ARRAY_KEY,
  DATABASE_ROW_SHARD_ROWS_MAP_KEY,
  normalizeDatabaseShardManifest,
} from '@blocksuite/affine-model';
import { createServerDatabaseRootDocId } from '@streamlain/database-identity';
import {
  VIEW_CONFIG_COLUMN_DEFS,
  type ViewConfigRow,
  ViewConfigRowSchema,
} from '@streamlain/view-config';
import { LiveData, Store } from '@toeverything/infra';
import { Observable } from 'rxjs';
import { z } from 'zod';

import type { DocService } from '../../doc';

type AnyBlock = { id: string; props?: any; model?: any; children?: AnyBlock[] };
type AnyBsDoc = {
  getBlock: (id: string) => AnyBlock | null | undefined;
  addBlock: (flavour: string, props: any, parentId: string) => string;
  deleteBlock: (modelOrId: any) => void;
  transact: (cb: () => void) => void;
  slots?: {
    blockUpdated?: {
      subscribe: (cb: () => void) => { unsubscribe: () => void };
    };
  };
  workspace?: {
    id?: string;
    createDoc?: (docId: string) => AnyWorkspaceDoc | null | undefined;
    getDoc?: (docId: string) => {
      getStore?: (options: { id: string }) => AnyBsDoc | null | undefined;
      load?: () => unknown;
      ready?: boolean;
    } | null;
    registerDoc?: (docId: string) => unknown;
  };
};

type AnyWorkspaceDoc = {
  getStore?: (options: { id: string }) => AnyBsDoc | null | undefined;
  load?: () => unknown;
  ready?: boolean;
};

type DatabaseRuntime = {
  dataSource: ReturnType<typeof createDatabaseBlockDataSource>;
  doc: AnyBsDoc;
  hostModel: any;
  model: any;
};

type ShardReadResult = {
  cellsByRow: Record<
    string,
    Record<string, { columnId: string; value: unknown }>
  >;
  rowIds: string[];
};

const OBJECT_FIELDS: ReadonlySet<string> = new Set(
  VIEW_CONFIG_COLUMN_DEFS.filter(d => {
    const zt = unwrap(d.zodType);
    return zt instanceof z.ZodObject || zt instanceof z.ZodRecord;
  }).map(d => d.field as string)
);
const JSON_FIELDS: ReadonlySet<string> = new Set([
  'bindings',
  'contentDbRef',
  'customAppConfig',
]);
const REQUIRED_NULLABLE_FIELDS = [
  'contentDbRef',
  'seoCanonicalCol',
  'seoMetaCol',
  'seoOgCol',
  'seoSlugCol',
  'themeRef',
] as const;
const OPTIONAL_UNDEFINED_FIELDS = ['slugPrefix'] as const;

function unwrap(t: z.ZodTypeAny): z.ZodTypeAny {
  let x: z.ZodTypeAny = t;
  while (
    x instanceof z.ZodDefault ||
    x instanceof z.ZodNullable ||
    x instanceof z.ZodOptional
  ) {
    x =
      x instanceof z.ZodDefault
        ? (x._def.innerType as z.ZodTypeAny)
        : x._def.innerType;
  }
  return x;
}

function encodeFieldValue(field: string, value: unknown): unknown {
  if (OBJECT_FIELDS.has(field) && value !== null && value !== undefined) {
    return JSON.stringify(value);
  }
  return value;
}

function decodeFieldValue(field: string, raw: unknown): unknown {
  if (!OBJECT_FIELDS.has(field) && !JSON_FIELDS.has(field)) return raw;
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export class ViewConfigRowStore extends Store {
  readonly rows$: LiveData<ViewConfigRow[]>;
  private runtimeCache: DatabaseRuntime | null = null;

  constructor(
    private readonly docService: DocService,
    private readonly databaseBlockId: string
  ) {
    super();

    this.rows$ = LiveData.from<ViewConfigRow[]>(
      new Observable<ViewConfigRow[]>(subscriber => {
        let runtime: DatabaseRuntime | null = null;
        let runtimeSub: { unsubscribe?: () => void } | undefined;
        let observedStorageModel: any | null = null;
        let storageReadyDispose: (() => void) | undefined;
        let observedShardDocKey = '';
        let shardDocDisposers: Array<() => void> = [];
        const cleanupShardDocObservers = () => {
          for (const dispose of shardDocDisposers) dispose();
          shardDocDisposers = [];
          observedShardDocKey = '';
        };
        const syncShardDocObservers = (nextRuntime: DatabaseRuntime) => {
          const docIds = this.getShardDocIds(nextRuntime);
          const key = docIds.join('\n');
          if (key === observedShardDocKey) return;
          cleanupShardDocObservers();
          observedShardDocKey = key;
          for (const docId of docIds) {
            const doc = this.ensureWorkspaceDoc(nextRuntime, docId);
            if (!doc) continue;
            const onUpdate = () => emitRows();
            doc.spaceDoc?.on?.('update', onUpdate);
            shardDocDisposers.push(() =>
              doc.spaceDoc?.off?.('update', onUpdate)
            );
          }
        };
        const attachRuntime = () => {
          const nextStorageModel = this.getHostModel();
          if (nextStorageModel !== observedStorageModel) {
            storageReadyDispose?.();
            storageReadyDispose = undefined;
            observedStorageModel = nextStorageModel;
            if (nextStorageModel) {
              try {
                storageReadyDispose = observeDatabaseStorageModelReady(
                  nextStorageModel,
                  () => emitRows()
                );
              } catch (error) {
                console.warn(
                  '[ViewConfigRowStore] database storage readiness observer unavailable',
                  error
                );
              }
            }
          }
          const nextRuntime = this.getDatabaseRuntime();
          if (!nextRuntime) return;
          if (runtime !== nextRuntime) {
            runtimeSub?.unsubscribe?.();
            cleanupShardDocObservers();
            runtime = nextRuntime;
            runtimeSub = runtime.doc.slots?.blockUpdated?.subscribe(() =>
              emitRows()
            );
          }
          syncShardDocObservers(nextRuntime);
        };
        const emitRows = (_dependency?: unknown) => {
          attachRuntime();
          subscriber.next(this.safeReadRows());
        };
        const hostSub = this.getBlockSuiteDoc()?.slots?.blockUpdated?.subscribe(
          () => emitRows()
        );
        const detachedRootSub =
          this.getDetachedRootDoc()?.slots?.blockUpdated?.subscribe(() =>
            emitRows()
          );
        emitRows();
        return () => {
          runtimeSub?.unsubscribe?.();
          cleanupShardDocObservers();
          storageReadyDispose?.();
          hostSub?.unsubscribe();
          detachedRootSub?.unsubscribe();
        };
      }),
      []
    );
  }

  getRow$(viewId: string): LiveData<ViewConfigRow | null> {
    return this.rows$.map(rows => rows.find(r => r.viewId === viewId) ?? null);
  }

  get defaultRow$(): LiveData<ViewConfigRow | null> {
    return this.rows$.map(rows => rows.find(r => r.isDefault) ?? null);
  }

  createRow(row: Omit<ViewConfigRow, 'viewId'>, viewId?: string): string {
    const runtime = this.getDatabaseRuntime();
    if (!runtime) {
      throw new Error('ViewConfigRowStore.createRow: no database runtime');
    }
    const paragraphId = runtime.doc.addBlock(
      'affine:paragraph',
      {
        ...(viewId ? { id: viewId } : {}),
        text: new Text(row.name),
        type: 'text',
      },
      runtime.model.id
    );

    runtime.doc.transact(() => {
      const props = runtime.model.props as
        | {
            cells?: Record<
              string,
              Record<string, { columnId: string; value: unknown }>
            >;
          }
        | undefined;
      if (!props) return;
      const cells = props.cells ?? {};
      if (!cells[paragraphId]) cells[paragraphId] = {};
      cells[paragraphId].title = {
        columnId: 'title',
        value: row.name,
      };
      const columnMap = this.resolveColumnMap();
      for (const def of VIEW_CONFIG_COLUMN_DEFS) {
        if (def.field === 'name' || def.field === 'viewId') continue;
        const rawValue = (row as Record<string, unknown>)[def.field as string];
        if (rawValue === undefined) continue;
        const columnId = columnMap[def.field as string];
        if (!columnId) continue;
        const encoded = encodeFieldValue(def.field as string, rawValue);
        cells[paragraphId][columnId] = { columnId, value: encoded };
      }
      runtime.model.cells = cells;
    });

    return paragraphId;
  }

  updateRow(viewId: string, patch: Partial<ViewConfigRow>): void {
    for (const [field, value] of Object.entries(patch)) {
      if (value === undefined) continue;
      this.updateField(viewId, field as keyof ViewConfigRow, value as never);
    }
  }

  updateField<K extends keyof ViewConfigRow>(
    viewId: string,
    field: K,
    value: ViewConfigRow[K]
  ): void {
    const runtime = this.getDatabaseRuntime();
    if (!runtime) {
      throw new Error('ViewConfigRowStore.updateField: no database runtime');
    }
    const childBlock = runtime.doc.getBlock(viewId);
    const columnMap = this.resolveColumnMap();
    const columnId = field === 'name' ? 'title' : columnMap[field as string];
    if (!columnId) {
      throw new Error(
        `ViewConfigRowStore.updateField: column "${String(field)}" missing`
      );
    }
    this.requireWritableRow(runtime, viewId);

    runtime.doc.transact(() => {
      if (field === 'name') {
        const text = (childBlock?.model as any)?.text;
        if (text?.replace) {
          text.replace(0, text.length ?? 0, String(value ?? ''));
        } else if (text?.delete && text?.insert) {
          text.delete(0, text.length ?? 0);
          text.insert(0, String(value ?? ''));
        }
        this.writeCell(runtime, viewId, 'title', String(value ?? ''));
        return;
      }
      this.writeCell(
        runtime,
        viewId,
        columnId,
        encodeFieldValue(field as string, value)
      );
    });
  }

  removeRow(viewId: string): void {
    const runtime = this.getDatabaseRuntime();
    const childBlock = runtime?.doc.getBlock(viewId);
    if (!runtime || !childBlock?.model) return;
    runtime.doc.deleteBlock(childBlock.model);
  }

  private writeCell(
    runtime: DatabaseRuntime,
    rowId: string,
    columnId: string,
    value: unknown
  ): void {
    if (this.writeShardCell(runtime, rowId, columnId, value)) return;

    const childBlock = runtime.doc.getBlock(rowId);
    const props = runtime.model.props as Record<string, any>;
    const cells = props.cells ?? {};
    if (!childBlock?.model && !cells[rowId]) {
      throw new Error(`ViewConfigRowStore.updateField: row "${rowId}" missing`);
    }
    if (!cells[rowId]) cells[rowId] = {};
    cells[rowId][columnId] = {
      columnId,
      value,
    };
    runtime.model.cells = cells;
  }

  private writeShardCell(
    runtime: DatabaseRuntime,
    rowId: string,
    columnId: string,
    value: unknown
  ): boolean {
    const manifest = normalizeDatabaseShardManifest(
      (runtime.model.props as any)?.databaseShardManifest
    );
    if (!manifest) return false;
    for (const shard of manifest.shards ?? []) {
      const doc = this.ensureWorkspaceDoc(runtime, shard.serverDocId);
      const rowsMap = doc?.spaceDoc?.getMap?.(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
      const rowCells = rowsMap?.get?.(rowId);
      if (!rowCells?.set) continue;
      const existing = rowCells.get?.(columnId);
      if (existing?.set) {
        existing.set('columnId', columnId);
        existing.set('value', value);
      } else {
        rowCells.set(columnId, { columnId, value });
      }
      return true;
    }
    return false;
  }

  private requireWritableRow(runtime: DatabaseRuntime, rowId: string): void {
    const childBlock = runtime.doc.getBlock(rowId);
    const rootCells = ((runtime.model.props as any)?.cells ?? {}) as Record<
      string,
      unknown
    >;
    if (
      childBlock?.model ||
      rootCells[rowId] ||
      this.hasShardRow(runtime, rowId)
    ) {
      return;
    }
    throw new Error(`ViewConfigRowStore.updateField: row "${rowId}" missing`);
  }

  private hasShardRow(runtime: DatabaseRuntime, rowId: string): boolean {
    const manifest = normalizeDatabaseShardManifest(
      (runtime.model.props as any)?.databaseShardManifest
    );
    if (!manifest) return false;
    for (const shard of manifest.shards ?? []) {
      const doc = this.ensureWorkspaceDoc(runtime, shard.serverDocId);
      const rowsMap = doc?.spaceDoc?.getMap?.(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
      const rowCells = rowsMap?.get?.(rowId);
      if (rowCells?.set) return true;
    }
    return false;
  }

  private getBlockSuiteDoc(): AnyBsDoc | null {
    const anyDoc = this.docService.doc as any;
    return (anyDoc?.blockSuiteDoc as AnyBsDoc) ?? null;
  }

  private getHostModel(): any | null {
    const hostDoc = this.getBlockSuiteDoc();
    const hostBlock = hostDoc?.getBlock(this.databaseBlockId);
    if (hostBlock?.model) return hostBlock.model;

    // Custom-App owned databases are deliberately detached from the visible
    // page tree. A freshly provisioned client can therefore have the canonical
    // database root doc while the host-page reference has no hydrated model.
    // Resolve that root directly instead of depending on the creator tab's
    // transient in-memory block model.
    const rootDoc = this.getDetachedRootDoc();
    return rootDoc?.getBlock(this.databaseBlockId)?.model ?? null;
  }

  private getDetachedRootDoc(): AnyBsDoc | null {
    const hostDoc = this.getBlockSuiteDoc();
    const workspace = hostDoc?.workspace;
    const workspaceId = workspace?.id;
    if (!workspace || !workspaceId) return null;
    const rootDocId = createServerDatabaseRootDocId({
      workspaceId,
      databaseId: this.databaseBlockId,
    });
    let rootWorkspaceDoc = workspace.getDoc?.(rootDocId);
    if (!rootWorkspaceDoc) {
      workspace.registerDoc?.(rootDocId);
      rootWorkspaceDoc = workspace.getDoc?.(rootDocId);
    }
    if (!rootWorkspaceDoc) {
      rootWorkspaceDoc = workspace.createDoc?.(rootDocId);
    }
    if (rootWorkspaceDoc && !rootWorkspaceDoc.ready) {
      rootWorkspaceDoc.load?.();
    }
    return rootWorkspaceDoc?.getStore?.({ id: rootDocId }) ?? null;
  }

  private resolveColumnMap(): Record<string, string> {
    const runtime = this.getDatabaseRuntime();
    const columns = (runtime?.model?.props?.columns ?? []) as Array<{
      id: string;
      name: string;
    }>;
    const map: Record<string, string> = {};
    for (const c of columns) map[c.name] = c.id;
    return map;
  }

  private safeReadRows(): ViewConfigRow[] {
    try {
      return this.readRows();
    } catch (error) {
      console.warn('[ViewConfigRowStore] rows unavailable', error);
      return [];
    }
  }

  private readRows(): ViewConfigRow[] {
    const runtime = this.getDatabaseRuntime();
    if (!runtime) return [];
    const columnMap = this.resolveColumnMap();
    const inverseColumnMap: Record<string, string> = {};
    for (const [field, id] of Object.entries(columnMap))
      inverseColumnMap[id] = field;

    const children = (runtime.model.children ?? []) as AnyBlock[];
    const childIds = children.map(child => child.id);
    const shardManifest = normalizeDatabaseShardManifest(
      (runtime.model.props as any)?.databaseShardManifest
    );
    const hasShardManifest = shardManifest !== null;
    const manifestRowCount = Array.isArray(shardManifest?.shards)
      ? shardManifest.shards.reduce(
          (sum: number, shard: { rowCount?: unknown }) =>
            sum + (typeof shard.rowCount === 'number' ? shard.rowCount : 0),
          0
        )
      : 0;
    const shardRead =
      hasShardManifest && manifestRowCount > 0
        ? this.readShardRows(runtime, shardManifest)
        : { cellsByRow: {}, rowIds: [] };
    let rowIds = childIds;
    try {
      const rowCount =
        typeof runtime.dataSource.rowCount$?.value === 'number'
          ? runtime.dataSource.rowCount$.value
          : childIds.length;
      const rowWindow = runtime.dataSource.getRowsWindow({
        offset: 0,
        limit: Math.max(rowCount, childIds.length, 1),
      });
      if (rowWindow.rowIds.length > 0) {
        rowIds = rowWindow.rowIds;
      } else if (hasShardManifest && (rowCount > 0 || manifestRowCount > 0)) {
        rowIds = shardRead.rowIds;
      } else {
        rowIds = childIds;
      }
    } catch {
      rowIds = childIds;
    }
    const rootCells = ((runtime.model.props as any)?.cells ?? {}) as Record<
      string,
      Record<string, { columnId: string; value: unknown }>
    >;

    const rows: ViewConfigRow[] = [];
    for (const rowId of rowIds) {
      const child =
        runtime.doc.getBlock(rowId) ??
        children.find(candidate => candidate.id === rowId);
      const raw: Record<string, unknown> = { viewId: rowId };
      raw.name = this.readRowName(
        runtime,
        rowId,
        child,
        rootCells,
        shardRead.cellsByRow[rowId]?.title?.value
      );

      for (const [columnId, field] of Object.entries(inverseColumnMap)) {
        if (!field) continue;
        const shardCell = shardRead.cellsByRow[rowId]?.[columnId];
        const rootCellValue = rootCells[rowId]?.[columnId]?.value;
        let cellValue =
          shardCell !== undefined ? shardCell.value : rootCellValue;
        if (shardCell === undefined) {
          try {
            const dataSourceValue = runtime.dataSource.cellValueGet(
              rowId,
              columnId
            );
            if (dataSourceValue !== undefined) cellValue = dataSourceValue;
          } catch {
            cellValue = rootCellValue;
          }
        }
        raw[field] = decodeFieldValue(field, cellValue);
      }
      this.normalizeRawRow(raw);

      const result = ViewConfigRowSchema.safeParse(raw);
      if (result.success) {
        rows.push(result.data);
      } else {
        console.error(
          '[ViewConfigRowStore] invalid row, skipped',
          rowId,
          result.error.issues
        );
      }
    }
    return rows;
  }

  private normalizeRawRow(raw: Record<string, unknown>): void {
    for (const field of REQUIRED_NULLABLE_FIELDS) {
      if (raw[field] === undefined) raw[field] = null;
    }
    for (const field of OPTIONAL_UNDEFINED_FIELDS) {
      if (raw[field] === null) delete raw[field];
    }
    if (raw.customAppConfig === null) {
      delete raw.customAppConfig;
    }
    if (raw.bindings === undefined || raw.bindings === null) {
      raw.bindings = {};
    }
  }

  private readShardRows(
    runtime: DatabaseRuntime,
    manifest: any
  ): ShardReadResult {
    const rowIds: string[] = [];
    const cellsByRow: ShardReadResult['cellsByRow'] = {};
    for (const shard of manifest.shards ?? []) {
      const doc = this.ensureWorkspaceDoc(runtime, shard.serverDocId);
      if (!doc) continue;
      const rowsMap = doc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
      const rowOrder = doc.spaceDoc.getArray(
        DATABASE_ROW_SHARD_ORDER_ARRAY_KEY
      );
      const orderedIds = rowOrder
        .toArray()
        .map((rowId: unknown) => String(rowId));
      if (orderedIds.length === 0) continue;
      for (const rowId of orderedIds) {
        const rawCells = rowsMap.get(rowId) as
          | { forEach?: (cb: (value: unknown, key: string) => void) => void }
          | undefined;
        if (!rawCells?.forEach) continue;
        rowIds.push(rowId);
        const cells: Record<string, { columnId: string; value: unknown }> = {};
        rawCells.forEach((rawCell: any, columnId: string) => {
          const value =
            rawCell?.has?.('value') && rawCell?.get
              ? rawCell.get('value')
              : rawCell && typeof rawCell === 'object' && 'value' in rawCell
                ? rawCell.value
                : rawCell;
          const rawColumnId =
            rawCell?.has?.('columnId') && rawCell?.get
              ? rawCell.get('columnId')
              : rawCell && typeof rawCell === 'object' && 'columnId' in rawCell
                ? rawCell.columnId
                : columnId;
          cells[columnId] = { columnId: String(rawColumnId), value };
        });
        cellsByRow[rowId] = cells;
      }
    }
    return { cellsByRow, rowIds };
  }

  private getShardDocIds(runtime: DatabaseRuntime): string[] {
    const manifest = normalizeDatabaseShardManifest(
      (runtime.model.props as any)?.databaseShardManifest
    );
    if (!manifest) return [];
    const docIds: string[] = [];
    for (const shard of manifest.shards ?? []) {
      docIds.push(shard.serverDocId);
    }
    return docIds;
  }

  private ensureWorkspaceDoc(
    runtime: DatabaseRuntime,
    docId: string
  ): any | null {
    const workspace = runtime.model.store.workspace;
    let doc = workspace.getDoc(docId);
    if (!doc) {
      if (typeof workspace.registerDoc === 'function') {
        workspace.registerDoc(docId);
        doc = workspace.getDoc(docId);
      } else if (typeof workspace.createDoc === 'function') {
        doc = workspace.createDoc(docId);
      }
    }
    doc?.load?.();
    return doc ?? null;
  }

  private readRowName(
    runtime: DatabaseRuntime,
    rowId: string,
    child: AnyBlock | null | undefined,
    rootCells: Record<
      string,
      Record<string, { columnId: string; value: unknown }>
    >,
    shardTitle: unknown
  ): string {
    if (shardTitle !== undefined && shardTitle !== null) {
      return String(shardTitle);
    }
    try {
      const title = runtime.dataSource.cellValueGet(rowId, 'title');
      if (title !== undefined && title !== null) return String(title);
    } catch {
      // Fall through to legacy storage shapes.
    }
    const rootTitle = rootCells[rowId]?.title?.value;
    if (rootTitle !== undefined && rootTitle !== null) return String(rootTitle);
    const text = (child as any)?.props?.text ?? (child as any)?.text;
    return text?.toString?.() ?? '';
  }

  private getDatabaseRuntime(): DatabaseRuntime | null {
    const hostModel = this.getHostModel();
    if (!hostModel) return null;
    try {
      if (!isDatabaseStorageModelReady(hostModel)) return null;
    } catch {
      return null;
    }
    if (this.runtimeCache?.hostModel === hostModel) {
      return this.runtimeCache;
    }
    const dataSource = createDatabaseBlockDataSource(hostModel as any);
    const storageModel = dataSource.storageModel as any;
    this.runtimeCache = {
      dataSource,
      doc: storageModel.store as AnyBsDoc,
      hostModel,
      model: storageModel,
    };
    return this.runtimeCache;
  }
}
